import { useEffect, useState } from 'react';
import { Tree } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { ExplainedButton as Button } from '../explained-button';
import { Dialog } from '../dialogs/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ModuleFolderDto } from '../../general/dtos';
import { useApi } from '../../providers/api-provider';
import '../../assets/module-folders.css';

export const MODULE_DRAG_TYPE = 'application/x-module-keys';
const FOLDER_DRAG_TYPE = 'application/x-module-folder';

export function ModuleFolderTree({ folderId, onSelect, selectedKeys, onMoved }: {
    folderId: number | null;
    onSelect: (id: number | null) => void;
    selectedKeys: string[];
    onMoved: () => Promise<void>;
}) {
    const api = useApi();
    const [folders, setFolders] = useState<ModuleFolderDto[]>([]);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({ root: true });
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [action, setAction] = useState<'create' | 'rename' | 'delete' | 'move' | null>(null);
    const [name, setName] = useState('');
    const [destination, setDestination] = useState<number | null>(null);
    const [dropTarget, setDropTarget] = useState<string | null>(null);
    const current = folders.find(folder => folder.id === folderId);

    useEffect(() => {
        if (!api) return;
        let active = true;
        api.getModuleFolders().then(result => {
            if (active) { setFolders(result.data); setLoaded(true); }
        }).catch(err => { if (active) setError(err.response?.data?.message || 'Could not load folders.'); });
        return () => { active = false; };
    }, [api]);

    useEffect(() => {
        const ancestors: Record<string, boolean> = { root: true };
        let id = folderId;
        while (id !== null) {
            ancestors[String(id)] = true;
            const folder = folders.find(item => item.id === id);
            if (!folder) break;
            id = folder.parent_id;
        }
        setExpanded(previous => ({ ...previous, ...ancestors }));
    }, [folderId, folders]);

    useEffect(() => {
        const clearDropTarget = () => setDropTarget(null);
        document.addEventListener('dragend', clearDropTarget);
        return () => document.removeEventListener('dragend', clearDropTarget);
    }, []);

    if (!api) return <div>Loading folders?</div>;

    const run = async (operation: () => Promise<void>) => {
        setBusy(true);
        setError('');
        try { await operation(); setAction(null); }
        catch (err: any) { setError(err.response?.data?.message || err.message || 'Folder operation failed.'); }
        finally { setBusy(false); }
    };

    const moveModules = async (keys: string[], target: number | null) => {
        await api.moveModulesToFolder(keys, target);
        await onMoved();
    };

    const drop = (event: React.DragEvent, target: number | null) => {
        event.preventDefault();
        event.stopPropagation();
        setDropTarget(null);
        if (busy || !loaded) return;
        void run(async () => {
            const moduleData = event.dataTransfer.getData(MODULE_DRAG_TYPE);
            if (moduleData) {
                const keys: unknown = JSON.parse(moduleData);
                if (!Array.isArray(keys) || !keys.every(key => typeof key === 'string')) throw new Error('Invalid module selection.');
                await moveModules(keys, target);
            } else {
                const source = event.dataTransfer.getData(FOLDER_DRAG_TYPE);
                if (!source) return;
                const result = await api.updateModuleFolder(Number(source), { parent_id: target });
                setFolders(result.data);
            }
            setExpanded(prev => ({ ...prev, [target ?? 'root']: true }));
        });
    };

    const children = (parent: number | null): TreeNode[] => folders.filter(folder => folder.parent_id === parent).map(folder => ({
        key: String(folder.id), label: folder.name, icon: 'pi pi-folder', data: folder.id, children: children(folder.id),
        className: dropTarget === String(folder.id) ? 'module-folder-drop-active' : undefined
    }));
    const nodes: TreeNode[] = [{ key: 'root', label: 'Modules', icon: 'pi pi-home', data: null, children: children(null),
        className: dropTarget === 'root' ? 'module-folder-drop-active' : undefined }];
    const rowFolderKey = (row: HTMLElement) => row.querySelector<HTMLElement>('[data-module-folder-id]')?.dataset.moduleFolderId;
    const dragOverFolder = (event: React.DragEvent<HTMLDivElement>) => {
        if (busy || !loaded || ![MODULE_DRAG_TYPE, FOLDER_DRAG_TYPE].some(type => Array.from(event.dataTransfer.types).includes(type))) return;
        const key = rowFolderKey(event.currentTarget);
        if (key === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        setDropTarget(key);
    };
    const path = (folder: ModuleFolderDto): string => {
        const parts = [folder.name];
        let parent = folder.parent_id;
        while (parent !== null) {
            const ancestor = folders.find(item => item.id === parent);
            if (!ancestor) break;
            parts.unshift(ancestor.name);
            parent = ancestor.parent_id;
        }
        return 'Modules / ' + parts.join(' / ');
    };

    return <section className="module-folders" aria-label="Module folders" aria-busy={busy}>
        <div className="module-folder-toolbar admin-toolbar">
            <strong>Folders</strong>
            <Button label="New folder" icon="pi pi-folder-open" className="p-button-text p-button-sm" disabled={busy || !loaded}
                disabledReason={busy ? 'Wait for the folder operation to finish.' : 'Wait for the folders to finish loading.'}
                onClick={() => { setName(''); setAction('create'); }} />
            <Button label="Rename" className="p-button-text p-button-sm" disabled={busy || !current}
                disabledReason={busy ? 'Wait for the folder operation to finish.' : 'Select a folder to rename. The root folder cannot be renamed.'}
                onClick={() => { setName(current?.name ?? ''); setAction('rename'); }} />
            <Button label="Delete folder" className="p-button-text p-button-sm" disabled={busy || !current}
                disabledReason={busy ? 'Wait for the folder operation to finish.' : 'Select a folder to delete. The root folder cannot be deleted.'} onClick={() => setAction('delete')} />
            <Button label={`Move selected (${selectedKeys.length})`} className="p-button-text p-button-sm" disabled={busy || !loaded || !selectedKeys.length}
                disabledReason={busy ? 'Wait for the folder operation to finish.' : !loaded ? 'Wait for the folders to finish loading.' : 'Select one or more modules to move.'}
                onClick={() => { setDestination(folderId); setAction('move'); }} />
        </div>
        {error && <div role="alert" className="module-folder-error">{error}</div>}
        <Tree value={nodes} selectionMode="single" selectionKeys={String(folderId ?? 'root')}
            onSelectionChange={event => { if (!busy && loaded && event.value != null) onSelect(event.value === 'root' ? null : Number(event.value)); }}
            expandedKeys={expanded} onToggle={event => setExpanded(event.value)}
            pt={{ content: {
                className: 'module-folder-row',
                onDragEnterCapture: dragOverFolder,
                onDragOverCapture: dragOverFolder,
                onDragLeaveCapture: (event: React.DragEvent<HTMLDivElement>) => {
                    if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDropTarget(null);
                },
                onDropCapture: (event: React.DragEvent<HTMLDivElement>) => {
                    const key = rowFolderKey(event.currentTarget);
                    if (key !== undefined) drop(event, key === 'root' ? null : Number(key));
                }
            } }}
            nodeTemplate={node => <span className="module-folder-target" data-module-folder-id={node.data ?? 'root'} draggable={node.data !== null && !busy}
                onDragStart={event => { event.stopPropagation(); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData(FOLDER_DRAG_TYPE, String(node.data)); }}
                >{node.label}</span>} />
        <small>Drag modules or folders onto a folder to move them. Ctrl/Cmd or Shift selects multiple modules.</small>
        <Dialog header={action === 'create' ? 'New folder' : action === 'rename' ? 'Rename folder' : action === 'move' ? 'Move selected modules' : 'Delete folder'}
            visible={action !== null} onHide={() => { if (!busy) setAction(null); }} modal style={{ width: 'min(30rem, 95vw)' }}
            footer={<><Button label="Cancel" className="p-button-text" disabled={busy} disabledReason="Wait for the folder operation to finish." onClick={() => setAction(null)} />
                <Button label={action === 'delete' ? 'Delete' : action === 'move' ? 'Move' : 'Save'} loading={busy}
                    disabled={busy || ((action === 'create' || action === 'rename') && !name.trim())}
                    disabledReason={busy ? 'Wait for the folder operation to finish.' : 'Enter a folder name first.'}
                    onClick={() => void run(async () => {
                        if (action === 'create') {
                            setFolders((await api.createModuleFolder(name, folderId)).data);
                            setExpanded(prev => ({ ...prev, [folderId ?? 'root']: true }));
                        } else if (action === 'rename' && current) setFolders((await api.updateModuleFolder(current.id, { name })).data);
                        else if (action === 'delete' && current) {
                            setFolders((await api.deleteModuleFolder(current.id)).data);
                            onSelect(current.parent_id);
                        } else if (action === 'move') await moveModules(selectedKeys, destination);
                    })} /></>}>
            {error && <p role="alert" className="module-folder-error">{error}</p>}
            {(action === 'create' || action === 'rename') && <><label htmlFor="module-folder-name">Folder name</label>
                <InputText id="module-folder-name" value={name} onChange={event => setName(event.target.value)} maxLength={250} autoFocus disabled={busy} className="w-full" /></>}
            {action === 'delete' && <p>Delete “{current?.name}”? The folder must contain no modules or subfolders.</p>}
            {action === 'move' && <Dropdown aria-label="Destination folder" value={destination} onChange={event => setDestination(event.value)}
                options={[{ label: 'Modules (root)', value: null }, ...folders.map(folder => ({ label: path(folder), value: folder.id }))]}
                className="w-full" disabled={busy} />}
        </Dialog>
    </section>;
}
